// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Plan-Aktionen: aktiv setzen, anlegen, forken (Fork-on-Edit), löschen.
// Alles braucht Netz — Pläne baut man zu Hause, nicht zwischen zwei Sätzen.
import { pb } from "./pb"
import { getAll } from "./idb"
import { syncFromPocketBase } from "./data"
import type { Plan, PlanDay } from "./types"

function uid(): string {
  return pb.authStore.record?.id ?? ""
}

/** Aktiven Plan wählen (users.active_plan) und die lokale Anmeldung aktualisieren. */
export async function setActivePlan(planId: string): Promise<void> {
  if (!uid()) return
  const rec = await pb.collection("users").update(uid(), { active_plan: planId })
  pb.authStore.save(pb.authStore.token, rec)
  window.dispatchEvent(new CustomEvent("fithub:synced"))
}

/** Leeren eigenen Plan anlegen (für „Neuer Plan"). */
export async function createPlan(name: string): Promise<Plan> {
  const plan = await pb.collection("plans").create<Plan>({ name, description: "", owner: uid() })
  await syncFromPocketBase()
  return plan
}

/**
 * Fork-on-Edit: Plan + Tage + Items als eigene Kopie anlegen (owner = ich,
 * forked_from = Original). Das Original bleibt unangetastet. Gibt die neue Plan-Id zurück.
 */
export async function forkPlan(planId: string): Promise<string> {
  const [plans, days, items] = await Promise.all([getAll("plans"), getAll("planDays"), getAll("planDayItems")])
  const src = plans.find((p) => p.id === planId)
  if (!src) throw new Error("Plan nicht gefunden")

  const copy = await pb.collection("plans").create<Plan>({
    name: src.name.endsWith("(angepasst)") ? src.name : `${src.name} (angepasst)`,
    description: src.description,
    owner: uid(),
    forked_from: src.id,
  })
  const srcDays = days.filter((d) => d.plan === planId).sort((a, b) => a.sort - b.sort)
  for (const d of srcDays) {
    const dCopy = await pb.collection("plan_days").create<PlanDay>({
      plan: copy.id, code: d.code, name: d.name, sort: d.sort,
    })
    const srcItems = items.filter((i) => i.plan_day === d.id).sort((a, b) => a.sort - b.sort)
    for (const it of srcItems) {
      await pb.collection("plan_day_items").create({
        plan_day: dCopy.id, exercise: it.exercise, sort: it.sort, block: it.block,
        target_sets: it.target_sets, target_reps: it.target_reps, timer: it.timer,
        rest_sec: it.rest_sec || 0, pause_after_sec: it.pause_after_sec || 0,
      })
    }
  }
  await syncFromPocketBase()
  return copy.id
}

/** Eigenen Plan löschen (Kaskade räumt Tage + Items ab). */
export async function deletePlan(planId: string): Promise<void> {
  await pb.collection("plans").delete(planId)
  await syncFromPocketBase()
}

/** Anzeige-Label für die Herkunft eines Plans — bewusst anonym. */
export function planBadge(plan: Plan): "Vorlage" | "von einem Mitglied" | "dein Plan" {
  if (!plan.owner) return "Vorlage"
  return plan.owner === uid() ? "dein Plan" : "von einem Mitglied"
}
